import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useAuth } from './AuthContext';

// Default Agile labels
const DEFAULT_LABELS = {
  level1: 'Epic',
  level2: 'Feature',
  level3: 'Story',
  level4: 'Task',
};

const HierarchyContext = createContext(null);

export const HierarchyProvider = ({ children }) => {
  const { user, tenant } = useAuth();
  const [labels, setLabels] = useState(DEFAULT_LABELS);
  const [mode, setMode] = useState('agile');
  const [loading, setLoading] = useState(true);

  const tenantId = tenant?.id || user?.tenant?.id || user?.tenant_id;

  // Load hierarchy config
  const loadConfig = useCallback(async () => {
    if (!tenantId) {
      setLabels(DEFAULT_LABELS);
      setMode('agile');
      setLoading(false);
      return;
    }

    try {
      const res = await api.getHierarchyConfig(tenantId);
      setLabels(res.data.labels || DEFAULT_LABELS);
      setMode(res.data.mode || 'agile');
    } catch (error) {
      console.error('Failed to load hierarchy config:', error);
      setLabels(DEFAULT_LABELS);
      setMode('agile');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Get label for a specific level (1-4)
  const getLevelLabel = useCallback((level) => {
    switch (level) {
      case 1: return labels.level1;
      case 2: return labels.level2;
      case 3: return labels.level3;
      case 4: return labels.level4;
      default: return `Level ${level}`;
    }
  }, [labels]);

  // Get label for an internal type (epic, feature, story, task)
  const getTypeLabel = useCallback((type) => {
    const typeLower = (type || '').toLowerCase();
    switch (typeLower) {
      case 'epic': return labels.level1;
      case 'feature': return labels.level2;
      case 'story': return labels.level3;
      case 'task': return labels.level4;
      default: return type;
    }
  }, [labels]);

  // Get plural label
  const getTypeLabelPlural = useCallback((type) => {
    const label = getTypeLabel(type);
    // Simple pluralization rules
    if (label.endsWith('y')) {
      return label.slice(0, -1) + 'ies';
    }
    if (label.endsWith('s') || label.endsWith('x') || label.endsWith('ch') || label.endsWith('sh')) {
      return label + 'es';
    }
    return label + 's';
  }, [getTypeLabel]);

  // Get internal type from display label
  const getTypeFromLabel = useCallback((label) => {
    const labelLower = (label || '').toLowerCase();
    if (labelLower === labels.level1.toLowerCase()) return 'epic';
    if (labelLower === labels.level2.toLowerCase()) return 'feature';
    if (labelLower === labels.level3.toLowerCase()) return 'story';
    if (labelLower === labels.level4.toLowerCase()) return 'task';
    return label;
  }, [labels]);

  // Update hierarchy config (admin only)
  const updateConfig = useCallback(async (newMode, customLabels = null) => {
    if (!tenantId) return;

    try {
      await api.updateHierarchyConfig(tenantId, {
        mode: newMode,
        custom_labels: customLabels
      });
      await loadConfig();
    } catch (error) {
      console.error('Failed to update hierarchy config:', error);
      throw error;
    }
  }, [tenantId, loadConfig]);

  const value = {
    labels,
    mode,
    loading,
    getLevelLabel,
    getTypeLabel,
    getTypeLabelPlural,
    getTypeFromLabel,
    updateConfig,
    refresh: loadConfig,
    // Convenience accessors
    epicLabel: labels.level1,
    featureLabel: labels.level2,
    storyLabel: labels.level3,
    taskLabel: labels.level4,
  };

  return (
    <HierarchyContext.Provider value={value}>
      {children}
    </HierarchyContext.Provider>
  );
};

export const useHierarchy = () => {
  const context = useContext(HierarchyContext);
  if (!context) {
    throw new Error('useHierarchy must be used within a HierarchyProvider');
  }
  return context;
};

export default HierarchyContext;
